import React, { useEffect, useMemo, useState } from 'react'
import StatusBadge from './StatusBadge'
import moment from 'moment/moment'
import Pagination from '../../../components/Pagination'
import { MdCircle  } from "react-icons/md";

const HeaderCell = ({ children, className = '' }) => (
  <th className={`px-4 py-3 text-left text-sm font-medium text-white/80 ${className}`}>{children}</th>
)

const Cell = ({ children, className = '' }) => (
  <td className={`px-2 py-2 text-sm dark:text-white/90 text-customBlack/90 ${className}`}>{children}</td>
)

const EventsTable = ({
  rows = [],
  selectedId,
  onSelect,
  isMobile,
  showOddsDetails = false,
}) => {
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(8)

  const { safePage, totalPages, pageRows } = useMemo(() => {
    const totalItems = rows.length
    const clampedPageSize = Math.max(pageSize, 1)
    const totalPages = Math.max(1, Math.ceil(totalItems / clampedPageSize))
    const safePage = Math.min(Math.max(page, 1), totalPages)
    const startIndex = totalItems === 0 ? 0 : (safePage - 1) * clampedPageSize
    const endIndex = totalItems === 0 ? 0 : Math.min(startIndex + clampedPageSize, totalItems)
    const pageRows = rows.slice(startIndex, endIndex)
    return { safePage, totalPages, pageRows }
  }, [rows, page, pageSize])

  useEffect(() => {
    if (safePage !== page) {
      setPage(safePage)
    }
  }, [safePage, page])

  const handlePageChange = (nextPage) => {
    const clampedPage = Math.min(Math.max(nextPage, 1), totalPages)
    setPage(clampedPage)
  }

  const handlePageSizeChange = (size) => {
    setPageSize(size)
    setPage(1)
  }

  return (
    <div className="overflow-x-auto shadow-md rounded-md dark:bg-custom_dark_primary/60 bg-customFontMilkWhite ring-1 dark:ring-custom_dark_secondary/50 ring-customBlack/5">
      <table className="min-w-full table-auto">
        <thead className="bg-white/5">
          <tr className="bg-custom_dark_primary dark:bg-custom_dark_secondary/60">
            <HeaderCell className={`${isMobile ? 'text-xs' : 'text-sm'}`}>ID</HeaderCell>
            <HeaderCell className={`${isMobile ? 'text-xs' : 'text-sm'}`}>League</HeaderCell>
            <HeaderCell className={`${isMobile ? 'text-xs' : 'text-sm'}`}>Event</HeaderCell>
            {showOddsDetails ? (
              <>
                <HeaderCell className={`text-center ${isMobile ? 'text-xs' : 'text-sm'}`}>Handicap</HeaderCell>
                <HeaderCell className={`text-center ${isMobile ? 'text-xs' : 'text-sm'}`}>Goal Line</HeaderCell>
                <HeaderCell className={`text-center ${isMobile ? 'text-xs' : 'text-sm'}`}>Odds</HeaderCell>
              </>
            ) : null}
            <HeaderCell className={`${isMobile ? 'text-xs' : 'text-sm'}`}>Status</HeaderCell>
          </tr>
        </thead>
        <tbody>
          {
            pageRows.length === 0 ? (
              <td
                colSpan={8}
                className="px-4 py-8 text-center text-sm font-medium text-customSecondaryText dark:text-customMuted"
              >
                No events available for the selected date.
              </td>
            ) :
              pageRows.map((r,i) => {
                const updatedAt = r.updatedAt === "0001-01-01T00:00:00" ? "-" : moment(r.updatedAt).format('YYYY-MM-DD hh:mm a');
                const selected = r.eventId === selectedId
                const odds = r.odds ?? {}
                const goalOdds = r.goalOdds ?? {}
                const myanOdds = Array.isArray(r.myanOdds) ? r.myanOdds : []
                const homeHandicap = String(odds.homeHandicap ?? '')
                const awayHandicap = String(odds.awayHandicap ?? '')
                const handicapDisplay = (value) => (value ? `(${value})` : '(--)')
                const homeOddValue =
                  typeof odds.home === 'number'
                    ? odds.home.toFixed(2)
                    : odds.home ?? '-'
                const awayOddValue =
                  typeof odds.away === 'number'
                    ? odds.away.toFixed(2)
                    : odds.away ?? '-'
                const goalOverValue =
                  typeof goalOdds.over === 'number'
                    ? goalOdds.over.toFixed(2)
                    : goalOdds.over ?? '-'
                const goalUnderValue =
                  typeof goalOdds.under === 'number'
                    ? goalOdds.under.toFixed(2)
                    : goalOdds.under ?? '-'
                const goalHandicapValue = goalOdds.handicap ?? '-'
                const formatMyanmarOdd = (index) => {
                  const value = myanOdds[index]
                  if (typeof value === 'number') {
                    return value
                  }
                  return value ?? '-'
                }

                return (
                  <tr
                    key={i}
                    onClick={() => onSelect(r.eventId)}
                    className={
                      'cursor-pointer dark:border-custom_dark_secondary/10 border-customBlack/5 transition-colors dark:hover:bg-white/5 hover:bg-customBlack/5' +
                      (selected ? ' bg-customBlack/5 dark:bg-white/5 border-l-4 border-customAccentBlue dark:border-customAccent' : '')
                    }
                  >
                    <Cell className={`whitespace-nowrap font-medium dark:text-white/90 text-customBlack/90 ${isMobile ? 'text-xs' : 'text-xs'}`}>
                     <div className='flex flex-col'>
                      <span>{r.eventId}</span>
                        <div className='flex items-center px-1 py-0.5 rounded text-customSecondaryText dark:text-customMuted'>
                          { updatedAt === "-" ? null : <MdCircle size={8} className='text-green-500 mr-1'/> }
                          <span className="text-[10px] ml-[2px]">{updatedAt}</span>
                        </div>
                    </div>
                    </Cell>
                    <Cell className="truncate">
                      <div className="flex flex-col">
                        <span className={`font-medium truncate ${isMobile ? 'text-xs' : 'text-[13px]'}`}>{r.leagueName}</span>
                        <span className="text-xs dark:text-customMuted text-gray-500">{moment(r.startTime).format('YYYY-MM-DD hh:mm a')}</span>
                      </div>
                    </Cell>
                    <Cell className="truncate flex justify-between">
                      <div className={`flex flex-col font-semibold ${isMobile ? 'text-xs' : 'text-[13px]'}`}>
                        <span className={`${homeHandicap.includes('-') ? 'text-customFontRed' : 'text-inherit'}`}>{r.homeTeam}</span>
                        <span className={`${awayHandicap.includes('-') ? 'text-customFontRed' : 'text-inherit'}`}>{r.awayTeam}</span>
                      </div>
                      <div className="flex flex-col items-center gap-1">
                        <div className="flex items-center gap-1">
                          {/* <span className="text-[10px] uppercase tracking-wide text-customBlack/60 dark:text-white/50">
                        HT
                      </span> */}
                          <span className="inline-flex min-w-[2.5rem] justify-center rounded-full bg-customAccent/10 px-2 text-xs font-semibold text-customAccent">
                            {r.score?.home === 909 ? 0 : r.score.home}
                          </span>
                        </div>
                        <div className="flex items-center gap-1">
                          {/* <span className="text-[10px] uppercase tracking-wide text-customBlack/60 dark:text-white/50">
                        AT
                      </span> */}
                          <span className="inline-flex min-w-[2.5rem] justify-center rounded-full bg-customAccent/10 px-2 text-xs font-semibold text-customAccent">
                            {r.score?.away === 909 ? 0 : r.score.away}
                          </span>
                        </div>
                      </div>
                    </Cell>
                    {showOddsDetails ? (
                      <>
                        <Cell className="text-left font-semibold">
                          <div className="flex flex-col gap-1 text-xs">
                            <div className={`flex items-center justify-start gap-1 ${homeHandicap.includes('-') ? 'text-customFontRed' : 'dark:text-white/90 text-customBlack/70'}`}>
                              <span className='text-[10px] text-customMuted'>H</span>
                              <span>{homeOddValue}</span>
                              <span className="w-[68px]">{handicapDisplay(homeHandicap)}</span>
                            </div>
                            <div className={`flex items-center justify-start gap-1 ${awayHandicap.includes('-') ? 'text-customFontRed' : 'dark:text-white/90 text-customBlack/70'}`}>
                              <span className='text-[10px] text-customMuted'>A</span>
                              <span>{awayOddValue}</span>
                              <span className="w-[68px]">{handicapDisplay(awayHandicap)}</span>
                            </div>
                          </div>
                        </Cell>
                        <Cell className="text-left font-semibold">
                          <div className="flex flex-col gap-1 text-xs">
                            <div className="flex items-center justify-start gap-1 dark:text-white/90 text-customBlack/70">
                              <span className='text-[10px] text-customMuted'>O</span>
                              <span>{goalOverValue}</span>
                              <span className="w-14">{handicapDisplay(goalHandicapValue)}</span>
                            </div>
                            <div className="flex items-center justify-start gap-1 dark:text-white/90 text-customBlack/70">
                              <span className='text-[10px] text-customMuted'>U</span>
                              <span>{goalUnderValue}</span>
                              <span className="w-14">{handicapDisplay(goalHandicapValue)}</span>
                            </div>
                          </div>
                        </Cell>
                        <Cell className="text-left">
                          <div className="flex flex-col gap-2 text-xs">
                            <div className="flex items-center justify-center gap-6 dark:text-white/90 text-customBlack">
                              <span className="text-right font-bold">{formatMyanmarOdd(0)}</span>
                            </div>
                            <div className="flex items-center justify-center gap-6 dark:text-white/90 text-customBlack">
                              <span className="text-right font-bold">{formatMyanmarOdd(1)}</span>
                            </div>
                          </div>
                        </Cell>
                      </>
                    ) : null}
                    <Cell>
                      <StatusBadge value={r.status} />
                    </Cell>
                  </tr>
                )
              })
          }
        </tbody>
      </table>
      <Pagination
        page={safePage}
        pageSize={pageSize}
        totalItems={rows.length}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        pageSizeOptions={[8, 10, 20, 50]}
      />
    </div>
  )
}

export default EventsTable
